import type { CaptureResult } from 'posthog-js';
import { logger } from '../utils/core/logger';
import { canExportAnalytics, getAnalyticsConsentGeneration } from './consent';

const PAGE_EVENT_ENDPOINT_PATH = '/events/page-lifecycle';
// Match the gateway limit while staying within browser keepalive request limits.
const MAX_ENVELOPE_BYTES = 60 * 1024;
const RETRY_DELAY_MS = 1_000;

interface PageEventEnvelope {
    schema_version: 1;
    events: Array<{
        uuid: string;
        event: '$pageview' | '$pageleave';
        timestamp: string;
        properties: Record<string, unknown>;
        $set?: Record<string, unknown>;
        $set_once?: Record<string, unknown>;
    }>;
}

interface PageEventMirrorOptions {
    apiHost: string | null;
    fetchImpl?: typeof globalThis.fetch;
    navigatorImpl?: Pick<Navigator, 'sendBeacon'>;
    scheduleRetry?: (callback: () => void, delayMs: number) => void;
}

function isPageEvent(event: CaptureResult): event is CaptureResult & {
    event: '$pageview' | '$pageleave';
} {
    return event.event === '$pageview' || event.event === '$pageleave';
}

function resolveEndpoint(apiHost: string | null): string | null {
    if (!apiHost) {
        return null;
    }

    try {
        return new URL(PAGE_EVENT_ENDPOINT_PATH, apiHost).toString();
    } catch (error) {
        logger.warn('Unable to resolve the first-party page-event endpoint', error);
        return null;
    }
}

function envelopeByteLength(value: string): number {
    return new TextEncoder().encode(value).byteLength;
}

/**
 * Sends page lifecycle events through Rose's first-party endpoint and removes them from
 * the PostHog pipeline. Every non-page event passes through unchanged.
 */
export class PageEventMirror {
    private readonly endpoint: string | null;
    private readonly fetchImpl: typeof globalThis.fetch | undefined;
    private readonly navigatorImpl: Pick<Navigator, 'sendBeacon'> | undefined;
    private readonly scheduleRetry: (callback: () => void, delayMs: number) => void;

    constructor(options: PageEventMirrorOptions) {
        this.endpoint = resolveEndpoint(options.apiHost);
        this.fetchImpl =
            options.fetchImpl ??
            (typeof globalThis.fetch === 'function'
                ? globalThis.fetch.bind(globalThis)
                : undefined);
        this.navigatorImpl =
            options.navigatorImpl ??
            (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function'
                ? navigator
                : undefined);
        this.scheduleRetry =
            options.scheduleRetry ??
            ((callback, delayMs) => {
                globalThis.setTimeout(callback, delayMs);
            });
    }

    public beforeSend(event: CaptureResult | null): CaptureResult | null {
        if (!canExportAnalytics()) return null;
        if (!event || !isPageEvent(event)) {
            return event;
        }

        try {
            this.deliver(event);
        } catch (error) {
            // before_send is synchronous. Delivery errors are observable, but PostHog is no
            // longer a page-event fallback: the first-party path owns these events globally.
            logger.warn('Unable to initiate first-party page-event delivery', error);
        }

        return null;
    }

    private deliver(event: CaptureResult): void {
        if (!this.endpoint || !isPageEvent(event)) {
            return;
        }

        const timestamp = event.timestamp?.toISOString();
        if (!event.uuid || !timestamp) {
            logger.warn('Skipping a first-party page event without a canonical UUID or timestamp');
            return;
        }

        const envelope: PageEventEnvelope = {
            schema_version: 1,
            events: [
                {
                    uuid: event.uuid,
                    event: event.event,
                    timestamp,
                    properties: event.properties,
                    ...(event.$set ? { $set: event.$set } : {}),
                    ...(event.$set_once ? { $set_once: event.$set_once } : {}),
                },
            ],
        };
        const body = JSON.stringify(envelope);

        if (envelopeByteLength(body) > MAX_ENVELOPE_BYTES) {
            logger.warn('Skipping an oversized first-party page-event envelope', {
                uuid: event.uuid,
                event: event.event,
            });
            return;
        }

        if (event.event === '$pageleave' && this.trySendBeacon(body)) {
            return;
        }

        this.tryFetch(body, true);
    }

    private trySendBeacon(body: string): boolean {
        if (!this.endpoint || !this.navigatorImpl) {
            return false;
        }

        try {
            return this.navigatorImpl.sendBeacon(this.endpoint, body);
        } catch (error) {
            logger.warn('First-party page-event beacon failed; falling back to fetch', error);
            return false;
        }
    }

    private tryFetch(
        body: string,
        allowRetry: boolean,
        generation = getAnalyticsConsentGeneration()
    ): boolean {
        if (!canExportAnalytics() || generation !== getAnalyticsConsentGeneration()) return false;
        if (!this.endpoint || !this.fetchImpl) {
            return false;
        }

        try {
            const request = this.fetchImpl(this.endpoint, {
                method: 'POST',
                body,
                headers: { 'Content-Type': 'text/plain' },
                keepalive: true,
            });

            void request
                .then((response) => {
                    if (!response.ok) {
                        if (allowRetry) {
                            this.retryFetch(body, generation);
                            return;
                        }
                        logger.warn('First-party page-event delivery failed after retry', {
                            status: response.status,
                        });
                    }
                })
                .catch((error: unknown) => {
                    if (allowRetry) {
                        this.retryFetch(body, generation);
                        return;
                    }
                    logger.warn('First-party page-event delivery failed after retry', error);
                });
            return true;
        } catch (error) {
            if (allowRetry) {
                this.retryFetch(body, generation);
                return true;
            }
            logger.warn('First-party page-event delivery failed after retry', error);
            return false;
        }
    }

    private retryFetch(body: string, generation: number): void {
        this.scheduleRetry(() => {
            this.tryFetch(body, false, generation);
        }, RETRY_DELAY_MS);
    }
}
